/**
 * Parameter validation for the Grossman-Miller model
 */

import { ModelParams, PARAM_CONSTRAINTS, ValidationResult } from './types';

/**
 * Validate all model parameters against constraints
 */
export function validateParams(params: ModelParams): ValidationResult {
  const errors: Partial<Record<keyof ModelParams, string>> = {};

  // Validate each parameter
  for (const [key, constraint] of Object.entries(PARAM_CONSTRAINTS)) {
    const paramKey = key as keyof ModelParams;
    const value = params[paramKey];

    if (typeof value !== 'number' || isNaN(value)) {
      errors[paramKey] = 'Must be a valid number';
      continue;
    }

    if (value < constraint.min) {
      errors[paramKey] = `Must be at least ${constraint.min}`;
      continue;
    }

    if (value > constraint.max) {
      errors[paramKey] = `Must be at most ${constraint.max}`;
      continue;
    }

    if ('integer' in constraint && constraint.integer && !Number.isInteger(value)) {
      errors[paramKey] = 'Must be a whole number';
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate a single parameter value
 */
export function validateParam(
  key: keyof ModelParams,
  value: number
): string | null {
  const constraint = PARAM_CONSTRAINTS[key];

  if (typeof value !== 'number' || isNaN(value)) {
    return 'Must be a valid number';
  }

  if (value < constraint.min) {
    return `Must be at least ${constraint.min}`;
  }

  if (value > constraint.max) {
    return `Must be at most ${constraint.max}`;
  }

  if ('integer' in constraint && constraint.integer && !Number.isInteger(value)) {
    return 'Must be a whole number';
  }

  return null;
}

/**
 * Clamp a parameter value to its valid range
 */
export function clampParam(key: keyof ModelParams, value: number): number {
  const constraint = PARAM_CONSTRAINTS[key];
  let clamped = Math.max(constraint.min, Math.min(constraint.max, value));

  if ('integer' in constraint && constraint.integer) {
    clamped = Math.round(clamped);
  }

  return clamped;
}
